import { useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { testimonials } from '@/data/apartments';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const TestimonialCarousel = () => {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent((c) => c === 0 ? testimonials.length - 1 : c - 1);
  const next = () => setCurrent((c) => c === testimonials.length - 1 ? 0 : c + 1);

  const review = testimonials[current];

  return (
    <section className="relative bg-secondary/20 py-24 md:py-32">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16 text-center">

          <p className="font-serif-elegant uppercase tracking-[0.3em] text-primary/70 text-base font-bold">
            Testimonials
          </p>
          <h2 className="mt-3 font-serif text-3xl tracking-wide text-foreground md:text-5xl">
            {t.testimonials.title}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
            {t.testimonials.subtitle}
          </p>
        </motion.div>

        <div className="mx-auto max-w-3xl">
          <div className="glass-card gold-border relative rounded-lg px-8 py-12 text-center md:px-16">
            <Quote className="mx-auto mb-6 h-8 w-8 text-primary/30" />

            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}>

                <div className="mb-4 flex items-center justify-center gap-1">
                  {[...Array(review.rating)].map((_, i) =>
                  <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  )}
                </div>
                <p className="font-serif-elegant text-lg italic leading-relaxed text-foreground/90 md:text-xl">
                  "{review.text}"
                </p>
                <p className="mt-6 font-serif text-base text-primary">{review.name}</p>
                <p className="text-xs text-muted-foreground">
                  {review.country} · {review.apartment}
                </p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                onClick={prev}
                aria-label="Previous testimonial"
                className="rounded-full border border-border/50 p-2 text-muted-foreground transition-colors hover:border-primary hover:text-primary">

                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex gap-1">
                {testimonials.map((_, i) =>
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  aria-label={`Go to testimonial ${i + 1}`}
                  className="relative flex items-center justify-center p-2"
                >
                  <span className={`block h-2 rounded-full transition-all ${
                    i === current ? 'w-6 bg-primary' : 'w-2 bg-muted-foreground/30'}`
                  } />
                </button>

                )}
              </div>
              <button
                onClick={next}
                aria-label="Next testimonial"
                className="rounded-full border border-border/50 p-2 text-muted-foreground transition-colors hover:border-primary hover:text-primary">

                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialCarousel;